/**
 * Wires the connectivity monitor to the replay engine. See plan
 * issue 6.2 / design §5.6.
 *
 * Two triggers:
 *
 *   1. **Offline → online transition.** Subscribes via
 *      `onOfflineToOnlineTransition()` from the connectivity monitor.
 *      Fires every time the device transitions, but the replay
 *      engine itself is concurrency-safe (a second call while a
 *      replay is in flight is a no-op), so duplicate triggers are
 *      cheap.
 *
 *   2. **App startup.** If we're online AND there are pending
 *      sessions when the component mounts, fire once. Covers the
 *      edge case the design specifically calls out: a controller
 *      who closes the browser while offline and reopens later when
 *      already online — no transition event will fire, but the
 *      ready_to_sync rows are still in IndexedDB.
 *
 * Gated behind `enable_offline_checks` so non-offline tenants never
 * exercise the replay engine. The component is mounted inside
 * <ConfigProvider> in RequireAuth.tsx alongside the other offline
 * guards.
 */

import { useEffect } from "react";

import useConfig from "../actions/Tenants/config/configHook";
import {
    onOfflineToOnlineTransition,
    useConnectivity,
} from "./connectivityMonitor";
import { isOfflineChecksEnabled } from "./db";
import { processQueue } from "./replayEngine";

export function OfflineReplayDriver(): null {
    const config = useConfig();
    const enabled = isOfflineChecksEnabled(config);
    const { isOnline, pendingSessions } = useConnectivity();

    // Trigger 1: offline→online transition.
    useEffect(() => {
        if (!enabled) return undefined;
        return onOfflineToOnlineTransition(() => {
            processQueue();
        });
    }, [enabled]);

    // Trigger 2: app startup with pending sessions while already
    // online. Fires once when the conditions are met. The replay
    // engine's `processing` guard prevents this from racing the
    // transition trigger.
    useEffect(() => {
        if (!enabled || !isOnline || pendingSessions === 0) return;
        processQueue();
    }, [enabled, isOnline, pendingSessions]);

    return null;
}
